import type {
  WalletAppleRow,
  WalletAssetUrls,
  WalletBarcodeSymbology,
  WalletBrandAssetSlot,
  WalletContentField,
  WalletFieldFormat,
  WalletPassDesignDocument,
  WalletPassKind,
  WalletPlatform,
  WalletStampOverlay,
} from "@lemmonhq/types";
import { walletBindingToken, type WalletBindingValues } from "./bindings";

/**
 * Resolves a design against one pass's binding values into exactly what a
 * platform will show. Every renderer — the `.pkpass` builder, the Google
 * class/object builder, the Collected card and the manager preview — is a thin
 * mapper over this output, so previews and production cannot drift apart.
 */

export type ResolvedWalletField = {
  key: string;
  label: string;
  /** Display string, formatted per binding type. */
  value: string;
  /** Unformatted value when the field is exactly one binding, e.g. an ISO date. */
  raw: string | number | null;
  format: WalletFieldFormat;
  change_message: string | null;
};

export type ResolvedWalletLink = { key: string; label: string; url: string };

export type ResolvedWalletBarcode = {
  symbology: WalletBarcodeSymbology;
  message: string;
  alt_text: string;
};

type WalletPassViewBase = {
  kind: WalletPassKind;
  enabled: boolean;
  logo_text: string;
  colors: WalletPassDesignDocument["brand"]["colors"];
  /** Resolved asset URLs; null when the slot is empty or the asset is gone. */
  assets: Record<WalletBrandAssetSlot, string | null>;
  asset_refs: WalletPassDesignDocument["brand"]["assets"];
  barcode: ResolvedWalletBarcode | null;
  links: ResolvedWalletLink[];
  stamps: WalletStampOverlay | null;
};

export type AppleWalletPassView = WalletPassViewBase & {
  platform: "apple";
  style: WalletPassDesignDocument["apple"]["style"];
  rows: Record<WalletAppleRow, ResolvedWalletField[]>;
  poster: WalletPassDesignDocument["apple"]["poster"];
  relevance_date: string | null;
  use_restaurant_location: boolean;
  sharing_prohibited: boolean;
  grouping_identifier: string;
  suppress_strip_shine: boolean;
};

export type GoogleWalletPassView = WalletPassViewBase & {
  platform: "google";
  header: ResolvedWalletField | null;
  subheader: ResolvedWalletField | null;
  rows: ResolvedWalletField[][];
  modules: ResolvedWalletField[];
};

export type BrowserWalletPassView = WalletPassViewBase & {
  platform: "browser";
  primary: ResolvedWalletField | null;
  secondary: ResolvedWalletField[];
  show_progress: boolean;
};

export type WalletPassView = {
  apple: AppleWalletPassView;
  google: GoogleWalletPassView;
  browser: BrowserWalletPassView;
};

export type ResolvePassViewInput = {
  kind: WalletPassKind;
  document: WalletPassDesignDocument;
  bindings: WalletBindingValues;
  assetUrls?: WalletAssetUrls;
  locale?: string;
};

const SAFE_LINK = /^(https?:\/\/|tel:|mailto:)\S+$/i;

const APPLE_ROWS: WalletAppleRow[] = [
  "header",
  "primary",
  "secondary",
  "auxiliary",
  "back",
];

const formatBinding = (
  value: string | number,
  type: WalletFieldFormat,
  locale: string,
): string => {
  if (type === "date") {
    const time = Date.parse(String(value));
    if (!Number.isNaN(time)) {
      return new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "short",
        year: "numeric",
      }).format(new Date(time));
    }
  }
  if (type === "number" && value !== "" && Number.isFinite(Number(value))) {
    return new Intl.NumberFormat(locale).format(Number(value));
  }
  return String(value);
};

/** Resolves `{{token}}`s, formatting each token by its registry type. */
export const resolveWalletDisplayTemplate = (
  template: string,
  kind: WalletPassKind,
  bindings: WalletBindingValues,
  locale = "en-US",
): string =>
  template
    .replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_match, token: string) => {
      const value = bindings[token];
      if (value == null || value === "") return "";
      const type = walletBindingToken(kind, token)?.type ?? "text";
      return formatBinding(value, type, locale);
    })
    .trim();

/** The token when a template is exactly one binding, e.g. "{{coins.balance}}". */
const singleToken = (template: string): string | null =>
  template.trim().match(/^\{\{\s*([\w.]+)\s*\}\}$/)?.[1] ?? null;

const resolveField = (
  field: WalletContentField,
  kind: WalletPassKind,
  bindings: WalletBindingValues,
  locale: string,
): ResolvedWalletField | null => {
  const value = resolveWalletDisplayTemplate(
    field.value,
    kind,
    bindings,
    locale,
  );
  if (!value) return null;
  const token = singleToken(field.value);
  const raw = token != null ? (bindings[token] ?? null) : null;
  return {
    key: field.key,
    label: resolveWalletDisplayTemplate(field.label, kind, bindings, locale),
    value,
    raw,
    format:
      field.format ??
      (token ? (walletBindingToken(kind, token)?.type ?? "text") : "text"),
    change_message: field.change_message ?? null,
  };
};

/** Resolves every platform's view in one pass over the design. */
export const resolvePassViews = (input: ResolvePassViewInput): WalletPassView => {
  const { kind, document, bindings } = input;
  const locale = input.locale ?? "en-US";
  const assetUrls = input.assetUrls ?? {};
  const template = (value: string) =>
    resolveWalletDisplayTemplate(value, kind, bindings, locale);

  const fields = new Map<string, ResolvedWalletField>();
  for (const field of document.content.fields) {
    const resolved = resolveField(field, kind, bindings, locale);
    if (resolved) fields.set(field.key, resolved);
  }
  const pick = (keys: string[]) =>
    keys.flatMap((key) => {
      const field = fields.get(key);
      return field ? [field] : [];
    });
  const one = (key: string | null) => (key ? (fields.get(key) ?? null) : null);

  const assetUrl = (slot: WalletBrandAssetSlot) => {
    const ref = document.brand.assets[slot];
    return ref ? (assetUrls[ref.asset_id] ?? null) : null;
  };

  const barcodeMessage = document.content.barcode
    ? template(document.content.barcode.message)
    : "";

  const base = {
    kind,
    logo_text: template(document.brand.logo_text),
    colors: document.brand.colors,
    assets: {
      icon: assetUrl("icon"),
      logo: assetUrl("logo"),
      artwork: assetUrl("artwork"),
    },
    asset_refs: document.brand.assets,
    // A barcode whose message resolves empty cannot be scanned; drop it.
    barcode:
      document.content.barcode && barcodeMessage
        ? {
            symbology: document.content.barcode.symbology,
            message: barcodeMessage,
            alt_text: template(document.content.barcode.alt_text),
          }
        : null,
    // Links render as hrefs and Wallet back fields; only web, phone and email
    // targets are allowed, so a template can never produce a script URL.
    links: document.content.links.flatMap((link) => {
      const url = template(link.url);
      return SAFE_LINK.test(url)
        ? [{ key: link.key, label: template(link.label), url }]
        : [];
    }),
    stamps: kind === "coin" && document.stamps?.enabled ? document.stamps : null,
  };

  const relevance = template(document.apple.relevance.date);
  const rows = Object.fromEntries(
    APPLE_ROWS.map((row) => [row, pick(document.apple.rows[row])]),
  ) as Record<WalletAppleRow, ResolvedWalletField[]>;

  return {
    apple: {
      ...base,
      platform: "apple",
      enabled: document.apple.enabled,
      style: document.apple.style,
      rows,
      poster: document.apple.poster,
      relevance_date: relevance || null,
      use_restaurant_location: document.apple.relevance.use_restaurant_location,
      sharing_prohibited: document.apple.sharing_prohibited,
      grouping_identifier: template(document.apple.grouping_identifier),
      suppress_strip_shine: document.apple.suppress_strip_shine,
    },
    google: {
      ...base,
      platform: "google",
      enabled: document.google.enabled,
      header: one(document.google.header),
      subheader: one(document.google.subheader),
      rows: document.google.rows
        .map((row) => pick(row))
        .filter((row) => row.length > 0),
      modules: pick(document.google.modules),
    },
    browser: {
      ...base,
      platform: "browser",
      enabled: document.browser.enabled,
      primary: one(document.browser.primary),
      secondary: pick(document.browser.secondary),
      show_progress: document.browser.show_progress,
    },
  };
};

/** One platform's view. */
export const resolvePassView = <P extends WalletPlatform>(
  input: ResolvePassViewInput,
  platform: P,
): WalletPassView[P] => resolvePassViews(input)[platform];
